import type { DefaultContext } from '@apollo/client';
import type { DialogId } from 'common/Account/dialogSettingsRegistry';
import { useDialogSettings } from 'common/Account/useDialogSettings';
import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

type UseSuppressibleDialogConfirmArgs = {
  dialogSettingId: DialogId;
  settingsContext: DefaultContext;
  open: boolean;
  onConfirm: () => undefined | Promise<void> | void;
  onSuppressionChange?: (suppressed: boolean) => void | Promise<void>;
};

type UseSuppressibleDialogConfirmResult = {
  shouldDisplayDialog: boolean;
  isDontShowAgainChecked: boolean;
  isUpdatingVisibility: boolean;
  handleConfirm: () => Promise<void>;
  handleDontShowAgainChange: (event: ChangeEvent<HTMLInputElement>) => void;
};

export const useSuppressibleDialogConfirm = ({
  dialogSettingId,
  settingsContext,
  open,
  onConfirm,
  onSuppressionChange,
}: UseSuppressibleDialogConfirmArgs): UseSuppressibleDialogConfirmResult => {
  const { dialogVisibilityById, handleToggleDialogVisibility } = useDialogSettings({
    context: settingsContext,
  });
  const isDialogVisible = dialogVisibilityById[dialogSettingId] ?? true;

  const [isDontShowAgainChecked, setIsDontShowAgainChecked] = useState(false);
  const [isUpdatingVisibility, setIsUpdatingVisibility] = useState(false);

  // Tracks whether this dialog session has already confirmed, so we do not
  // re-run the auto-confirm path if visibility flips while the dialog is open.
  const hasConfirmedRef = useRef(false);

  // Treat each parent-driven dialog session as a fresh interaction so a past
  // auto-confirm or checkbox choice does not leak into the next dialog open.
  useEffect(() => {
    hasConfirmedRef.current = false;
    setIsDontShowAgainChecked(false);

    if (!open) {
      setIsUpdatingVisibility(false);
    }
  }, [open]);

  const persistSuppressionPreference = useCallback(async () => {
    if (!isDontShowAgainChecked || isUpdatingVisibility) {
      return;
    }

    setIsUpdatingVisibility(true);
    try {
      await handleToggleDialogVisibility(dialogSettingId, false);
      await onSuppressionChange?.(true);
    } catch {
      setIsDontShowAgainChecked(false);
    } finally {
      setIsUpdatingVisibility(false);
    }
  }, [
    dialogSettingId,
    isDontShowAgainChecked,
    handleToggleDialogVisibility,
    isUpdatingVisibility,
    onSuppressionChange,
  ]);

  const handleConfirm = useCallback(async () => {
    hasConfirmedRef.current = true;
    await persistSuppressionPreference();
    await onConfirm();
  }, [onConfirm, persistSuppressionPreference]);

  const shouldAutoConfirm = open && !isDialogVisible && !hasConfirmedRef.current;

  // When the dialog is requested open but this setting has already been suppressed,
  // confirm immediately instead of rendering the modal. The ref prevents duplicate
  // confirms if visibility changes again during the same dialog session.
  useEffect(() => {
    if (!shouldAutoConfirm) {
      return;
    }

    void handleConfirm();
  }, [handleConfirm, shouldAutoConfirm]);

  const handleDontShowAgainChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setIsDontShowAgainChecked(event.target.checked);
  }, []);

  return {
    shouldDisplayDialog: open && isDialogVisible,
    isDontShowAgainChecked,
    isUpdatingVisibility,
    handleConfirm,
    handleDontShowAgainChange,
  };
};
